import { dayjs } from '@/utilities';

export const CALENDAR_VIEW_MODES = ['day', 'week', 'month', 'quarter'] as const;

export type CalendarViewMode = (typeof CALENDAR_VIEW_MODES)[number];
export type CalendarDate = ReturnType<typeof dayjs>;

export interface CalendarPeriod {
    from: string;
    label: string;
    start: CalendarDate;
    to: string;
    end: CalendarDate;
}

export const getCalendarPeriod = (value: CalendarDate, view: CalendarViewMode): CalendarPeriod => {
    if (view === 'day') {
        return {
            end: value.endOf('day'),
            from: value.format('YYYY-MM-DD'),
            label: value.format('dddd, MMMM D, YYYY'),
            start: value.startOf('day'),
            to: value.format('YYYY-MM-DD'),
        };
    }

    if (view === 'week') {
        const start = value.startOf('week');
        const end = value.endOf('week');

        return {
            end,
            from: start.format('YYYY-MM-DD'),
            label: `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`,
            start,
            to: end.format('YYYY-MM-DD'),
        };
    }

    if (view === 'quarter') {
        const start = value.startOf('quarter');
        const end = value.endOf('quarter');

        return {
            end,
            from: start.format('YYYY-MM-DD'),
            label: `Q${value.quarter()} ${value.year()}`,
            start,
            to: end.format('YYYY-MM-DD'),
        };
    }

    const start = value.startOf('month');
    const end = value.endOf('month');

    return {
        end,
        from: start.format('YYYY-MM-DD'),
        label: value.format('MMMM YYYY'),
        start,
        to: end.format('YYYY-MM-DD'),
    };
};

export const getCalendarQueryPeriod = (value: CalendarDate, view: CalendarViewMode): CalendarPeriod => {
    const period = getCalendarPeriod(value, view);

    if (view === 'month' || view === 'quarter') {
        const start = period.start.startOf('week');
        const end = period.end.endOf('week');

        return {
            ...period,
            end,
            from: start.format('YYYY-MM-DD'),
            start,
            to: end.format('YYYY-MM-DD'),
        };
    }

    return period;
};

export const shiftCalendarPeriod = (value: CalendarDate, view: CalendarViewMode, direction: -1 | 1): CalendarDate => {
    if (view === 'day') {
        return value.add(direction, 'day');
    }
    if (view === 'week') {
        return value.add(direction, 'week');
    }
    if (view === 'quarter') {
        return value.add(direction * 3, 'month');
    }

    return value.add(direction, 'month');
};
